import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import InfinityMark from '../components/InfinityMark';
import { useT } from '../lib/i18n';
import INFINITY_TRANSLATIONS from '../lib/infinity_translations.json';

const INFINITIES_DATA = [
  { n: 1, roman: 'I',   key: 'inf.1', src: '/infinity/1.png' },
  { n: 2, roman: 'II',  key: 'inf.2', src: '/infinity/2.png' },
  { n: 3, roman: 'III', key: 'inf.3', src: '/infinity/3.png' },
  { n: 4, roman: 'IV',  key: 'inf.4', src: '/infinity/4.png' },
  { n: 5, roman: 'V',   key: 'inf.5', src: '/infinity/5.png' },
];

function Header({ left, right }) {
  return (
    <header className="w-full px-6 sm:px-10 lg:px-16 pt-10 sm:pt-16 lg:pt-20 pb-12 sm:pb-16 lg:pb-20 grid grid-cols-3 items-center gap-4">
      <div className="justify-self-start">{left}</div>
      <Link
        to="/"
        className="justify-self-center hover:opacity-70 transition-opacity duration-300"
        aria-label="Connect to Love — home"
      >
        <InfinityMark size={40} stroke={6} className="sm:hidden" />
        <InfinityMark size={48} stroke={6} className="hidden sm:block" />
      </Link>
      <div className="justify-self-end">{right}</div>
    </header>
  );
}

export default function Infinities() {
  const { t, lang } = useT();
  const [selected, setSelected] = useState(null);
  const INFINITIES = INFINITIES_DATA.map(i => ({ ...i, title: t(i.key) }));

  // Close detail view with ESC
  useEffect(() => {
    if (!selected) return;
    const onKey = (e) => { if (e.key === 'Escape') setSelected(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selected]);

  if (selected) {
    const item = INFINITIES.find((i) => i.n === selected);
    const trKey = String(selected);
    const trBundle = INFINITY_TRANSLATIONS[trKey] || {};
    const localized = trBundle[lang] || trBundle.en || null;
    return (
      <div className="min-h-screen flex flex-col fade-in" data-testid="infinity-detail" key={selected}>
        <Header
          left={
            <button
              type="button"
              onClick={() => setSelected(null)}
              className="text-[0.7rem] sm:text-xs tracking-[0.18em] uppercase text-[#2A2724] hover:opacity-70 transition-opacity duration-300"
              style={{ fontWeight: 700 }}
              data-testid="infinity-back-btn"
            >
              {t('nav.back')}
            </button>
          }
          right={
            <span
              className="text-[0.7rem] sm:text-xs tracking-[0.18em] uppercase text-[#2A2724] opacity-60"
              style={{ fontWeight: 700 }}
              data-testid="infinity-number-label"
            >
              {item.roman} · {item.title.toUpperCase()}
            </span>
          }
        />

        <main className="flex-1 w-full px-4 sm:px-10 pb-24 flex flex-col items-center">
          <img
            src={item.src}
            alt={`Infinity ${item.roman} — ${item.title}`}
            className="max-w-full max-h-[70vh] w-auto h-auto object-contain"
            data-testid={`infinity-large-${item.n}`}
          />

          {localized && Array.isArray(localized.sections) && lang !== 'en' && (
            <section
              className="w-full max-w-2xl mt-14 sm:mt-20 px-2 sm:px-6 text-[#2A2724]"
              data-testid={`infinity-text-${item.n}`}
            >
              {localized.title && (
                <h2
                  className="text-xs sm:text-sm tracking-[0.32em] uppercase opacity-60 text-center mb-10"
                  style={{ fontWeight: 700 }}
                  data-testid={`infinity-localized-title-${item.n}`}
                >
                  {localized.title}
                </h2>
              )}
              <div className="space-y-10">
                {localized.sections.map((s, idx) => (
                  <div key={idx} data-testid={`infinity-section-${item.n}-${idx}`}>
                    <h3
                      className="text-[0.72rem] sm:text-xs tracking-[0.28em] uppercase mb-3 opacity-90"
                      style={{ fontWeight: 700 }}
                    >
                      {s.heading}
                    </h3>
                    <p className="text-sm sm:text-base leading-relaxed opacity-80">
                      {s.body}
                    </p>
                  </div>
                ))}
              </div>
            </section>
          )}
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col fade-in" data-testid="infinities-screen">
      <Header
        left={
          <Link
            to="/"
            className="text-[0.7rem] sm:text-xs tracking-[0.18em] uppercase text-[#2A2724] hover:opacity-70 transition-opacity duration-300"
            style={{ fontWeight: 700 }}
            data-testid="infinities-back-link"
          >
            {t('nav.back')}
          </Link>
        }
        right={null}
      />

      <main className="flex-1 w-full px-6 sm:px-10 lg:px-16 pb-32">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-16">
            {INFINITIES.map(({ n, roman, title, src }) => (
              <button
                key={n}
                type="button"
                onClick={() => setSelected(n)}
                className="group bg-transparent border-0 p-0 cursor-pointer flex flex-col items-center justify-start py-6 opacity-90 hover:opacity-100 transition-opacity duration-500"
                data-testid={`infinity-card-${n}`}
              >
                <div className="w-full mb-6 overflow-hidden flex items-center justify-center">
                  <img
                    src={src}
                    alt={`Infinity ${roman} — ${title}`}
                    className="w-full h-auto object-contain"
                    loading="lazy"
                  />
                </div>
                <span
                  className="text-xs tracking-[0.32em] uppercase text-[#2A2724] font-bold"
                  data-testid={`infinity-label-${n}`}
                >
                  {roman} · {title}
                </span>
              </button>
            ))}
          </div>

          <div
            className="mt-32 sm:mt-40 mb-4 flex justify-center select-none pointer-events-none"
            aria-hidden="true"
            data-testid="love-equation-mark"
          >
            <span
              className="text-[#2A2724] tracking-[0.45em]"
              style={{
                opacity: 0.15,
                fontFamily: "'Cormorant Garamond', 'EB Garamond', Georgia, serif",
                fontStyle: 'italic',
                fontWeight: 400,
                fontSize: '1.15rem',
                letterSpacing: '0.45em',
              }}
            >
              E&nbsp;=&nbsp;mc<sup style={{ fontSize: '0.7em', verticalAlign: 'super' }}>2</sup>
            </span>
          </div>
        </div>
      </main>
    </div>
  );
}
